import { useState } from 'react';
import { Mail, Search, CheckCircle2, AlertCircle, Send, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import api from '../src/api';

interface TrackingActions {
  can_pay: boolean;
  can_resend: boolean;
  can_upload: boolean;
  can_request_material_link: boolean;
  material_uploaded: boolean;
}

interface TrackingDTO {
  event_name: string;
  status: string;
  role: string;
  payment_status: 'PENDING' | 'PAID';
  paid_at?: string | null;
  review_status: 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'N/A';
  actions: TrackingActions;
}

interface StatusTrackingViewProps {
  onBackToRegister: () => void;
}

export function StatusTrackingView({ onBackToRegister }: StatusTrackingViewProps) {
  const [email, setEmail] = useState('');
  const [folio, setFolio] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [trackingData, setTrackingData] = useState<TrackingDTO | null>(null);
  const [isResending, setIsResending] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !folio) return;

    setIsLoading(true);
    try {
      const response = await api.post('/registrations/tracking', { email, folio });
      setTrackingData(response.data);
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Error al consultar el estatus. Verifica tus datos.';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (!trackingData) return;
    setIsResending(true);
    try {
      const endpoint = trackingData.role === 'SPEAKER'
        ? '/registrations/resend-material-link'
        : '/registrations/resend-credential';
      const response = await api.post(endpoint, { email, folio });
      toast.success(response.data.message);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al reenviar.');
    } finally {
      setIsResending(false);
    }
  };

  if (trackingData) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <Button variant="ghost" onClick={() => setTrackingData(null)} className="gap-2 text-[#0f2744]">
          <ArrowLeft className="h-4 w-4" /> Nueva consulta
        </Button>

        <Card className="border-[#c9a227]/30 shadow-md">
          <CardHeader className="bg-[#0f2744] text-white">
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-base md:text-xl font-bold uppercase tracking-tight">
                  Estatus de Participante
                </CardTitle>
                <CardDescription className="text-gray-300 font-medium">
                  {trackingData.event_name}
                </CardDescription>
              </div>
              <Badge className={`${trackingData.payment_status === 'PAID' ? 'bg-green-600' : 'bg-[#c9a227]'} text-white border-none shadow-sm`}>
                {trackingData.payment_status === 'PAID' ? 'PAGADO' : 'PENDIENTE DE PAGO'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                <p className="text-[10px] md:text-xs text-gray-500 uppercase font-bold mb-1">Rol en el Evento</p>
                <p className="text-lg font-semibold text-[#0f2744]">
                  {trackingData.role === 'SPEAKER' ? 'PONENTE' : 'ASISTENTE'}
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-100">
                <p className="text-[10px] md:text-xs text-gray-500 uppercase font-bold mb-1">Estatus Academico</p>
                <div className="flex items-center gap-2">
                  {trackingData.review_status === 'ACCEPTED' && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                  <p className={`text-lg font-semibold ${trackingData.review_status === 'REJECTED' ? 'text-red-600' : 'text-[#0f2744]'}`}>
                    {trackingData.review_status === 'ACCEPTED'
                      ? 'APROBADO'
                      : trackingData.review_status === 'PENDING'
                        ? 'EN REVISION'
                        : trackingData.review_status === 'REJECTED'
                          ? 'CON OBSERVACIONES'
                          : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-gray-100">
              {trackingData.actions.can_pay && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex flex-col items-center text-center space-y-3">
                  <AlertCircle className="h-6 w-6 text-yellow-600" />
                  <p className="text-sm text-yellow-800 font-medium">
                    Tu propuesta fue aceptada. El siguiente paso es realizar el pago para continuar.
                  </p>
                  <Button className="bg-[#0f2744] hover:bg-[#1a3a5a] text-white w-full md:w-auto px-10">
                    IR A SYSWEB PARA PAGAR
                  </Button>
                </div>
              )}

              {trackingData.actions.can_resend && (
                <div className="p-6 bg-green-50/50 border border-green-200 rounded-xl flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="bg-green-600 rounded-full p-2">
                      <CheckCircle2 className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-green-900">Pago confirmado</h4>
                      <p className="text-sm text-green-700">Tu credencial digital esta lista.</p>
                    </div>
                  </div>
                  <Button
                    onClick={handleResend}
                    disabled={isResending}
                    className="bg-green-600 hover:bg-green-700 text-white min-w-[200px]"
                  >
                    {isResending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                    Reenviar a mi correo
                  </Button>
                </div>
              )}

              {trackingData.actions.can_request_material_link && (
                <div className="p-6 bg-[#0f2744]/5 border border-[#0f2744]/20 rounded-xl flex flex-col md:flex-row items-center justify-between gap-4">
                  <div>
                    <h4 className="font-bold text-[#0f2744]">Reenviar link de material</h4>
                    <p className="text-sm text-gray-700">
                      Se enviara un nuevo link de un solo uso (vigencia 48h) a tu correo registrado.
                    </p>
                  </div>
                  <Button
                    onClick={handleResend}
                    disabled={isResending}
                    className="bg-[#0f2744] hover:bg-[#1a3a5a] text-white min-w-[220px]"
                  >
                    {isResending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                    Reenviar link de material
                  </Button>
                </div>
              )}

              {trackingData.actions.material_uploaded && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-800 text-sm font-medium">
                  Material recibido. Credencial en proceso.
                </div>
              )}
            </div>
          </CardContent>
          <CardFooter className="bg-gray-50 border-t justify-center py-4">
            <p className="text-[10px] text-gray-400 font-medium uppercase tracking-widest">
              Portal Ciego UNACH - Seguridad de Datos
            </p>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto">
      <div className="text-center mb-10">
        <div className="inline-flex items-center justify-center bg-[#0f2744] rounded-2xl p-4 shadow-xl border-2 border-[#c9a227]/30 mb-6">
          <Search className="h-10 w-10 text-[#c9a227]" />
        </div>
        <h1 className="text-3xl font-black text-[#0f2744] uppercase tracking-tighter">
          Seguimiento <span className="text-[#c9a227]">CIU 2026</span>
        </h1>
      </div>

      <Card className="border-t-4 border-t-[#0f2744] shadow-2xl relative overflow-hidden">
        <CardHeader>
          <CardTitle className="text-lg md:text-xl font-bold text-[#0f2744] uppercase tracking-tight">Acceso al Portal</CardTitle>
          <CardDescription>
            Ingresa los datos proporcionados en tu correo de registro.
          </CardDescription>
        </CardHeader>
        <form onSubmit={handleSearch}>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-bold text-gray-700 ml-1">Correo Electrónico *</label>
              <div className="relative group">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  type="email"
                  placeholder="ejemplo@unach.mx"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 h-10 border-gray-200 focus:border-[#0f2744]"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-gray-700 ml-1">Folio de Registro *</label>
              <div className="relative group">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="CIU-2026-XXXX-XXXX"
                  value={folio}
                  onChange={(e) => setFolio(e.target.value.toUpperCase())}
                  className="pl-10 h-10 border-gray-200 focus:border-[#0f2744] uppercase font-mono"
                  required
                />
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex flex-col space-y-3">
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#0f2744] hover:bg-[#1a3a5a] text-white h-12 text-lg font-bold shadow-lg"
            >
              {isLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Consultar Mi Estatus'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              onClick={onBackToRegister}
              className="w-full text-gray-400 hover:text-gray-600"
            >
              Volver al inicio
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}
